<template>
  <Container padding-top full-width :class="$style['page']">
    <Breadcrumb :items="[{ label: 'Kleding verhuur', to: { name: 'kleding' } }, { label: 'Reservering aanvragen' }]" />
    <h1 :class="$style['title']">Reservering aanvragen</h1>

    <!-- The bag comes back from the browser after the page has loaded -->
    <p v-if="!loaded" :class="$style['loading']" role="status">
      <Spinner size="sm" aria-hidden="true" /> Je reservering wordt geladen…
    </p>

    <!-- Sent -->
    <section v-else-if="sent" :class="$style['done']" aria-labelledby="reservation-heading">
      <Icon name="CheckCircle" :class="$style['done__icon']" />
      <h2 id="reservation-heading" ref="heading" tabindex="-1">Aanvraag verstuurd</h2>
      <p>
        Bedankt, {{ sent.name }}. Je krijgt een bevestiging op <strong>{{ sent.email }}</strong>.
        We nemen contact met je op om de reservering te bevestigen.
      </p>
      <dl :class="$style['review']">
        <div :class="$style['review__row']">
          <dt>Periode</dt>
          <dd>{{ sent.period }}</dd>
        </div>
        <div :class="$style['review__row']">
          <dt>Items</dt>
          <dd>
            <ul :class="$style['review__items']">
              <li v-for="item in sent.items" :key="item.id">{{ item.quantity }}× {{ item.title }}</li>
            </ul>
          </dd>
        </div>
      </dl>
      <Button variant="primary" :to="{ name: 'kleding' }" icon="ArrowRight" icon-pos="right">
        Verder kijken
      </Button>
    </section>

    <!-- Nothing chosen yet -->
    <section v-else-if="!items.length" :class="$style['empty']" aria-labelledby="reservation-heading">
      <h2 id="reservation-heading">Je hebt nog niets gekozen</h2>
      <p>Zoek in het assortiment wat je wilt huren en voeg het toe. Daarna vraag je hier alles in één keer aan.</p>
      <Button variant="primary" :to="{ name: 'kleding' }" icon="ArrowRight" icon-pos="right">
        Bekijk het assortiment
      </Button>
    </section>

    <template v-else>
      <ol :class="$style['steps']" aria-label="Stappen">
        <li
          v-for="(label, i) in STEPS"
          :key="label"
          :class="[$style['steps__step'], step === i + 1 && $style['steps__step--current'], step > i + 1 && $style['steps__step--done']]"
          :aria-current="step === i + 1 ? 'step' : undefined">
          <button v-if="step > i + 1" type="button" :class="$style['steps__link']" @click="goTo(i + 1)">
            <span :class="$style['steps__number']"><Icon name="Check" /></span>
            <span :class="$style['steps__label']">{{ label }}</span>
          </button>
          <span v-else :class="$style['steps__link']">
            <span :class="$style['steps__number']">{{ i + 1 }}</span>
            <span :class="$style['steps__label']">{{ label }}</span>
          </span>
        </li>
      </ol>

      <!-- A checkout: the step on the left, what you reserve on the right in view the whole time.
           Phones: the items under the period in the first step; the review lists them after that. -->
      <div :class="[$style['checkout'], step === 1 && $style['checkout--first']]">
      <form id="reservation-form" ref="formEl" :class="$style['form']" novalidate @submit.prevent="next">
        <!-- Step 1: when and what -->
        <section v-if="step === 1" :class="$style['section']" aria-labelledby="reservation-heading">
          <h2 id="reservation-heading" ref="heading" tabindex="-1">Wanneer wil je huren?</h2>
          <div :class="$style['dates']">
            <FormInput name="fromDate" type="date" label="Vanaf" :min="today" />
            <FormInput name="toDate" type="date" label="Tot en met" :min="values.fromDate || today" />
          </div>
          <p :class="[$style['availability'], availabilityState === 'short' && $style['availability--short']]" role="status">
            <Spinner v-if="availabilityState === 'checking'" size="sm" aria-hidden="true" />
            <Icon v-else-if="availabilityState === 'ok'" name="CheckCircle" />
            <Icon v-else-if="availabilityState === 'short' || availabilityState === 'error'" name="AlertTriangle" />
            {{ availabilityMessage }}
          </p>

        </section>

        <!-- Step 2: contact details -->
        <section v-else-if="step === 2" :class="$style['section']" aria-labelledby="reservation-heading">
          <h2 id="reservation-heading" ref="heading" tabindex="-1">Je gegevens</h2>
          <p :class="$style['section__intro']">We gebruiken je gegevens alleen om contact met je op te nemen over deze reservering.</p>
          <FormInput name="name" label="Naam" autocomplete="name" />
          <FormInput
            name="email"
            type="email"
            label="E-mailadres"
            autocomplete="email"
            inputmode="email"
            hint="Hier sturen we de bevestiging van je aanvraag naartoe." />
          <FormInput
            name="phoneNumber"
            type="tel"
            label="Telefoonnummer (optioneel)"
            autocomplete="tel"
            hint="Handig als we snel iets willen afstemmen." />
          <FormTextarea
            name="notes"
            label="Opmerking (optioneel)"
            rows="4"
            hint="Bijvoorbeeld maten, de gelegenheid of een vraag." />
        </section>

        <!-- Step 3: review -->
        <section v-else :class="$style['section']" aria-labelledby="reservation-heading">
          <h2 id="reservation-heading" ref="heading" tabindex="-1">Controleer je aanvraag</h2>
          <dl :class="$style['review']">
            <div :class="$style['review__row']">
              <dt>Periode</dt>
              <dd>{{ periodLabel }}</dd>
              <button type="button" :class="$style['review__edit']" @click="goTo(1)">Wijzig<span :class="$style['sr-only']"> periode</span></button>
            </div>
            <div :class="[$style['review__row'], $style['review__row--items']]">
              <dt>Items</dt>
              <dd>
                <ul :class="$style['review__items']">
                  <li v-for="item in items" :key="item.id">{{ item.quantity }}× {{ item.title }}</li>
                </ul>
              </dd>
              <button type="button" :class="$style['review__edit']" @click="goTo(1)">Wijzig<span :class="$style['sr-only']"> items</span></button>
            </div>
            <div :class="$style['review__row']">
              <dt>Gegevens</dt>
              <dd>
                {{ values.name }}<br>
                {{ values.email }}<template v-if="values.phoneNumber"><br>{{ values.phoneNumber }}</template>
              </dd>
              <button type="button" :class="$style['review__edit']" @click="goTo(2)">Wijzig<span :class="$style['sr-only']"> gegevens</span></button>
            </div>
            <div v-if="values.notes" :class="$style['review__row']">
              <dt>Opmerking</dt>
              <dd :class="$style['review__notes']">{{ values.notes }}</dd>
              <button type="button" :class="$style['review__edit']" @click="goTo(2)">Wijzig<span :class="$style['sr-only']"> opmerking</span></button>
            </div>
          </dl>
          <p :class="$style['section__intro']">
            Dit is een aanvraag, nog geen definitieve reservering. We nemen contact met je op om hem te bevestigen.
          </p>
          <div v-if="submitError" ref="errorEl" :class="$style['error']" role="alert" tabindex="-1">
            <p>{{ submitError }}</p>
            <Button v-if="submitConflict" variant="outline" type="button" @click="goTo(1)">
              Pas je items of periode aan
            </Button>
            <p v-else>
              Lukt het niet? Bel <a href="tel:0031621584520">06&nbsp;21&nbsp;58&nbsp;45&nbsp;20</a> of mail
              <a href="mailto:secretariaat@hengeloserevue.nl">secretariaat@hengeloserevue.nl</a>.
            </p>
          </div>
        </section>

      </form>

      <aside :class="$style['summary']" aria-labelledby="items-title">
        <h2 id="items-title" :class="$style['summary__title']">Je items</h2>
        <div :class="$style['items']">
          <ShoppingBagItem
            v-for="item in items"
            :key="item.id"
            :item="item"
            :availability="availabilities[item.id]"
            @remove="removeItem(item.id)"
            @update-quantity="qty => updateQuantity(item.id, qty)" />
        </div>
        <NuxtLink :to="{ name: 'kleding' }" :class="$style['more']">
          <Icon name="Plus" /> Meer toevoegen uit het assortiment
        </NuxtLink>
      </aside>

      <div :class="$style['actions']">
        <p v-if="blocker" :class="$style['blocker']" role="alert">
          <Icon name="AlertTriangle" /> {{ blocker }}
        </p>
        <Button v-if="step > 1" variant="ghost" icon="ArrowLeft" type="button" @click="goTo(step - 1)">
          Vorige
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="reservation-form"
          :loading="loading"
          :icon="step === 3 ? undefined : 'ArrowRight'"
          icon-pos="right"
          :class="$style['actions__next']">
          {{ nextLabel }}
        </Button>
      </div>
      </div>
    </template>
  </Container>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate'

interface Availability {
  total: number
  available: number
}

definePageMeta({
  layout: 'rental'
})
useHead({ title: 'Reservering aanvragen' })

// Three steps, kept in the address (?stap=2) so the browser's back button moves between them
const STEPS = ['Periode en items', 'Je gegevens', 'Controleren']
const CONTACT_KEY = 'reservation-contact'

const { $api } = useNuxtApp()
const route = useRoute()
const router = useRouter()
const { items, period, loaded, removeItem, updateQuantity, setPeriod, clear, itemCount } = useShoppingBag()

// One form across the steps; values stay when a step's fields unmount
const { values, setValues, setFieldError, setErrors } = useForm({
  initialValues: { fromDate: '', toDate: '', name: '', email: '', phoneNumber: '', notes: '' },
  keepValuesOnUnmount: true
})

const today = todayIso()
const step = computed(() => Math.min(Math.max(Number(route.query.stap) || 1, 1), 3))
const heading = ref<HTMLElement>()
const formEl = ref<HTMLFormElement>()
const blocker = ref('')

// ─── Period and availability ──────────────────────────────────────────────────
const availabilities = ref<Record<number, Availability>>({})
const availabilityState = ref<'idle' | 'checking' | 'ok' | 'short' | 'error'>('idle')
let availabilityRequest = 0
const ready = ref(false)

const periodValid = computed(() => !!values.fromDate && !!values.toDate && values.fromDate >= today && values.toDate >= values.fromDate)
const shortItems = computed(() => items.value.filter(item => {
  const availability = availabilities.value[item.id]
  return availability && availability.available < item.quantity
}))

async function checkAvailability () {
  const request = ++availabilityRequest
  availabilities.value = {}
  if (!periodValid.value || !items.value.length) {
    availabilityState.value = 'idle'
    return
  }
  availabilityState.value = 'checking'
  try {
    const results = await Promise.all(items.value.map(item =>
      ($api as typeof $fetch)<Availability>(`/products/${item.id}/availability`, {
        params: { fromDate: values.fromDate, toDate: values.toDate }
      }).then(availability => [item.id, availability] as const)
    ))
    // An older request that finishes late must not overwrite the answer for the current period
    if (request !== availabilityRequest) { return }
    availabilities.value = Object.fromEntries(results)
    availabilityState.value = shortItems.value.length ? 'short' : 'ok'
  } catch {
    if (request === availabilityRequest) { availabilityState.value = 'error' }
  }
}

// Changing a quantity doesn't need a new request, only a new verdict
watch(() => items.value.map(item => item.quantity), () => {
  blocker.value = ''
  if (availabilityState.value === 'ok' || availabilityState.value === 'short') {
    availabilityState.value = shortItems.value.length ? 'short' : 'ok'
  }
})
watch([() => values.fromDate, () => values.toDate, () => items.value.map(item => item.id).join()], () => {
  // Until the saved period is in the form, an empty form would overwrite it
  if (!ready.value) { return }
  blocker.value = ''
  setPeriod({ fromDate: values.fromDate ?? '', toDate: values.toDate ?? '' })
  if (values.fromDate || values.toDate) { validatePeriod(false) }
  checkAvailability()
})

const periodLabel = computed(() => formatPeriod(values.fromDate, values.toDate))

const availabilityMessage = computed(() => {
  switch (availabilityState.value) {
    case 'checking': return 'Beschikbaarheid controleren…'
    case 'ok': return `Alles is vrij van ${periodLabel.value}.`
    case 'short': {
      const count = shortItems.value.length
      return `${count === 1 ? 'Eén item is' : `${count} items zijn`} niet (genoeg) vrij in deze periode. Verlaag het aantal, haal het weg of kies andere data.`
    }
    case 'error': return 'De beschikbaarheid kon niet worden gecontroleerd. Probeer het zo nog eens.'
    default: return 'Kies je periode, dan zie je meteen of alles vrij is.'
  }
})

// Shows errors only for fields that have a value, unless the visitor tries to continue
function validatePeriod (all = true) {
  const { fromDate, toDate } = values
  let fromError: string | undefined
  let toError: string | undefined
  if (!fromDate) {
    fromError = all ? 'Kies de eerste dag.' : undefined
  } else if (fromDate < today) {
    fromError = 'Kies een dag vanaf vandaag.'
  }
  if (!toDate) {
    toError = all ? 'Kies de laatste dag.' : undefined
  } else if (fromDate && toDate < fromDate) {
    toError = 'De laatste dag ligt vóór de eerste.'
  }
  setFieldError('fromDate', fromError)
  setFieldError('toDate', toError)
  return !fromError && !toError && periodValid.value
}

// ─── Contact details ──────────────────────────────────────────────────────────
function validateContact () {
  const nameError = (values.name ?? '').trim().length < 2 ? 'Vul je naam in.' : undefined
  const emailError = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((values.email ?? '').trim()) ? undefined : 'Vul een geldig e-mailadres in, bijvoorbeeld naam@voorbeeld.nl.'
  const notesError = (values.notes ?? '').length > 2000 ? 'Houd je opmerking korter dan 2000 tekens.' : undefined
  setErrors({ name: nameError, email: emailError, notes: notesError })
  return !nameError && !emailError && !notesError
}

// ─── Moving between steps ─────────────────────────────────────────────────────
const nextLabel = computed(() => {
  if (step.value === 1) { return 'Verder naar je gegevens' }
  if (step.value === 2) { return 'Verder naar controleren' }
  return loading.value ? 'Versturen…' : 'Aanvraag versturen'
})

function goTo (target: number) {
  blocker.value = ''
  router.push({ query: { ...route.query, stap: target > 1 ? String(target) : undefined } })
}

function focusFirstError () {
  nextTick(() => formEl.value?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus())
}

async function next () {
  blocker.value = ''
  if (step.value === 1) {
    if (!validatePeriod()) {
      blocker.value = 'Kies eerst de periode waarin je wilt huren.'
      focusFirstError()
      return
    }
    if (availabilityState.value === 'checking') {
      blocker.value = 'Even geduld, we controleren nog of alles vrij is.'
      return
    }
    if (availabilityState.value === 'error') {
      await checkAvailability()
      if (availabilityState.value === 'error') {
        blocker.value = 'De beschikbaarheid kon niet worden gecontroleerd. Probeer het zo nog eens.'
        return
      }
    }
    if (availabilityState.value !== 'ok') {
      blocker.value = 'Niet alles is vrij in deze periode. Pas de aantallen of je periode aan.'
      return
    }
    goTo(2)
  } else if (step.value === 2) {
    if (!validateContact()) {
      blocker.value = 'Vul de gemarkeerde velden in.'
      focusFirstError()
      return
    }
    goTo(3)
  } else {
    await submit()
  }
}

// Each step starts at its heading, for keyboard and screen reader users too
watch(step, async () => {
  await nextTick()
  const top = formEl.value?.getBoundingClientRect().top
  if (top !== undefined && top < 0) {
    window.scrollTo({ top: window.scrollY + top - 120 })
  }
  heading.value?.focus({ preventScroll: true })
})

// Someone who reloads on a later step, or opens it directly, goes back to the first step that still needs work
watch(loaded, isLoaded => {
  if (!isLoaded) { return }
  let contact = {}
  try { contact = JSON.parse(sessionStorage.getItem(CONTACT_KEY) ?? '{}') } catch {}
  // Setting the period starts the availability check (see the watch above)
  setValues({ ...contact, fromDate: period.value.fromDate, toDate: period.value.toDate }, false)
  ready.value = true
  if (step.value > 1 && !periodValid.value) {
    router.replace({ query: { ...route.query, stap: undefined } })
  } else if (step.value > 2 && (values.name ?? '').trim().length < 2) {
    router.replace({ query: { ...route.query, stap: '2' } })
  }
}, { immediate: true })

// The contact details stay for this browser session only
watch(() => [values.name, values.email, values.phoneNumber, values.notes], ([name, email, phoneNumber, notes]) => {
  if (!ready.value) { return }
  blocker.value = ''
  try { sessionStorage.setItem(CONTACT_KEY, JSON.stringify({ name, email, phoneNumber, notes })) } catch {}
})

// ─── Sending ──────────────────────────────────────────────────────────────────
const loading = ref(false)
const submitError = ref('')
const submitConflict = ref(false)
const errorEl = ref<HTMLElement>()
const sent = ref<{ name: string, email: string, period: string, items: Array<{ id: number, title: string, quantity: number }> }>()

async function submit () {
  loading.value = true
  submitError.value = ''
  submitConflict.value = false
  try {
    await ($api as typeof $fetch)('/reservations', {
      method: 'POST',
      body: {
        name: values.name?.trim(),
        email: values.email?.trim(),
        phoneNumber: values.phoneNumber?.trim() || null,
        fromDate: values.fromDate,
        toDate: values.toDate,
        products: items.value.map(item => ({ id: item.id, quantity: item.quantity })),
        notes: values.notes?.trim() || null
      }
    })
    sent.value = {
      name: values.name!.trim(),
      email: values.email!.trim(),
      period: periodLabel.value,
      items: items.value.map(({ id, title, quantity }) => ({ id, title, quantity }))
    }
    clear()
    try { sessionStorage.removeItem(CONTACT_KEY) } catch {}
    await router.replace({ query: { ...route.query, stap: undefined } })
    await nextTick()
    // From the top, so the title and the confirmation are both in view
    window.scrollTo({ top: 0 })
    heading.value?.focus({ preventScroll: true })
  } catch (err: any) {
    // 409: someone else reserved the same items in the meantime
    submitConflict.value = err?.status === 409 || err?.response?.status === 409
    submitError.value = err?.data?.message ?? err?.data?.errors?.[0]?.message ?? 'Je aanvraag kon niet worden verstuurd. Probeer het nog eens.'
    if (submitConflict.value) { checkAvailability() }
    await nextTick()
    errorEl.value?.focus()
  } finally {
    loading.value = false
  }
}

// "12 t/m 15 februari 2027" or "30 januari t/m 2 februari 2027"
function formatPeriod (from?: string, to?: string) {
  if (!from || !to) { return '' }
  const date = (value: string) => {
    const [year, month, day] = value.split('-').map(Number)
    return new Date(year!, month! - 1, day)
  }
  const start = date(from)
  const end = date(to)
  const long = (d: Date, options: Intl.DateTimeFormatOptions) => d.toLocaleDateString('nl-NL', options)
  const endLabel = long(end, { day: 'numeric', month: 'long', year: 'numeric' })
  if (from === to) { return endLabel }
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
  const startLabel = sameMonth
    ? String(start.getDate())
    : long(start, start.getFullYear() === end.getFullYear() ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' })
  return `${startLabel} t/m ${endLabel}`
}
</script>

<style module>
.title {
  margin: var(--spacing-xl) 0 var(--spacing-xxl);
  font-size: var(--stage-title-page);
  line-height: 1;
}

.loading {
  display: flex;
  align-items: center;
  gap: var(--spacing);
  color: var(--text-subtle);
}

.empty,
.done {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-l);
  max-width: var(--stage-measure);
}

.empty p,
.done p {
  max-width: var(--stage-measure);
  margin: 0;
  font-size: var(--stage-text);
}

.done__icon {
  width: 3rem;
  height: 3rem;
  color: var(--primary-color);
}

/* One big title in capitals on the page; the rest bold but in sentence case */
.empty h2,
.done h2,
.section h2,
.summary__title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-large);
  line-height: 1.2;
  letter-spacing: 0;
  text-transform: none;
}

/* Progress */
.steps {
  display: flex;
  gap: var(--spacing-xl);
  margin: 0 0 var(--spacing-xxl);
  padding: 0 0 var(--spacing-l);
  border-bottom: var(--stage-hairline) solid var(--stage-line);
  list-style: none;
}

.steps__link {
  display: flex;
  align-items: center;
  gap: var(--spacing);
  padding: 0;
  border: 0;
  background: none;
  color: var(--text-subtle);
  font: inherit;
  font-size: var(--font-size-small);
  text-align: left;
}

button.steps__link {
  cursor: pointer;
}

button.steps__link:hover .steps__label {
  color: var(--text-base);
  text-decoration: underline;
}

.steps__number {
  display: grid;
  place-items: center;
  flex: none;
  width: 1.75rem;
  height: 1.75rem;
  border: 0.125rem solid currentcolor;
  border-radius: 50%;
  font-weight: var(--font-weight-bold);
}

.steps__number svg {
  width: 1rem;
  height: 1rem;
}

.steps__step--current .steps__link {
  color: var(--text-base);
  font-weight: var(--font-weight-bold);
}

.steps__step--current .steps__number {
  border-color: var(--primary-color);
  background: var(--primary-color);
  color: var(--stage-black);
}

.steps__step--done .steps__number {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

/* The step, the items next to it, the buttons under the step */
.checkout {
  display: grid;
  grid-template:
    "form summary" auto
    "actions summary" 1fr
    / minmax(0, 40rem) minmax(20rem, 26rem);
  justify-content: space-between;
  gap: var(--spacing-xxl) var(--spacing-xxxl);
}

.summary {
  grid-area: summary;
  position: sticky;
  top: calc(var(--stage-header) + var(--spacing-l));
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
  padding: var(--spacing-xl);
  background: var(--background-subtle);
}

/* Form */
.form {
  grid-area: form;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
}

.section h2:focus {
  outline: none;
}

.section__intro {
  max-width: var(--stage-measure);
  margin: 0;
  color: var(--text-subtle);
}

.dates {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--spacing-l);
}

.availability {
  display: flex;
  align-items: center;
  gap: var(--spacing);
  min-height: 1.5rem;
  margin: 0;
  color: var(--text-subtle);
}

.availability svg {
  flex: none;
  width: 1.25rem;
  height: 1.25rem;
}

.availability--short {
  color: var(--stage-error);
  font-weight: var(--font-weight-bold);
}

/* The items lay themselves out by the width they get (ShoppingBag/Item.vue) */
.items {
  container-type: inline-size;
  border-bottom: var(--stage-hairline) solid var(--stage-line-soft);
}

.more {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: var(--spacing);
  min-height: 2.75rem;
  color: var(--primary-color);
  font-weight: var(--font-weight-bold);
  text-decoration: none;
}

.more:hover {
  text-decoration: underline;
}

.more svg {
  width: 1.125rem;
  height: 1.125rem;
}

/* Review */
.review {
  margin: 0;
  border-bottom: var(--stage-hairline) solid var(--stage-line);
}

.review__row {
  display: grid;
  grid-template-columns: 8rem minmax(0, 1fr) auto;
  gap: var(--spacing) var(--spacing-xl);
  padding: var(--spacing-l) 0;
  border-top: var(--stage-hairline) solid var(--stage-line);
}

.review__row dt {
  color: var(--text-subtle);
  font-weight: var(--font-weight-bold);
}

.review__row dd {
  margin: 0;
}

/* The summary lists the items on a larger screen */
.review .review__row--items {
  display: none;
}

.review__items {
  margin: 0;
  padding: 0;
  list-style: none;
}

.review__notes {
  white-space: pre-line;
}

.review__edit {
  align-self: start;
  min-height: 2rem;
  padding: 0;
  border: 0;
  background: none;
  color: var(--primary-color);
  font: inherit;
  font-weight: var(--font-weight-bold);
  cursor: pointer;
}

.review__edit:hover {
  text-decoration: underline;
}

.sr-only {
  position: absolute;
  width: var(--stage-hairline);
  height: var(--stage-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.error {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-m);
  padding: var(--spacing-l);
  border: 0.125rem solid var(--stage-error);
}

.error p {
  margin: 0;
}

.error p:first-child {
  color: var(--stage-error);
  font-weight: var(--font-weight-bold);
}

.error a {
  color: var(--primary-color);
}

.blocker {
  display: flex;
  align-items: center;
  gap: var(--spacing);
  margin: 0;
  color: var(--stage-error);
  font-weight: var(--font-weight-bold);
}

.blocker svg {
  flex: none;
  width: 1.25rem;
  height: 1.25rem;
}

.actions {
  grid-area: actions;
  align-self: start;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-m);
}

.actions .blocker {
  flex-basis: 100%;
}

/* On narrow phones the label may wrap instead of being cut off */
.actions .actions__next {
  min-height: 3rem;
  margin-left: auto;
  line-height: 1.2;
  white-space: normal;
  text-align: center;
}

@media (width <= 60rem) {
  .checkout {
    grid-template:
      "form" auto
      "summary" auto
      "actions" auto
      / minmax(0, 1fr);
    gap: var(--spacing-xl);
  }

  .summary {
    position: static;
    padding: 0;
    background: none;
  }

  /* After the first step the review lists the items */
  .checkout:not(.checkout--first) .summary {
    display: none;
  }

  .review .review__row--items {
    display: grid;
  }
}

@media (width <= 40rem) {
  .steps {
    gap: var(--spacing-l);
  }

  /* Only the current step keeps its name; the others show their number */
  .steps__step:not(.steps__step--current) .steps__label {
    position: absolute;
    width: var(--stage-hairline);
    height: var(--stage-hairline);
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .dates {
    grid-template-columns: minmax(0, 1fr);
  }

  .review__row {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .review__row dd {
    grid-column: 1 / -1;
    grid-row: 2;
  }

  .actions .actions__next {
    width: 100%;
    margin-left: 0;
  }
}
</style>
